import type { ReactNode } from 'react';
import styles from './Badge.module.css';

/*
 * The four §5 tones — and the absence of a fifth is the point. §2 has no `info` tone, so a badge
 * cannot have an `info` variant; deriving the type from this array (rather than declaring both)
 * means the runtime list and the compile-time union cannot drift apart, and the test below asserts
 * against the list itself rather than against a hand-copied duplicate of it.
 */
export const BADGE_TONES = ['neutral', 'positive', 'notice', 'danger'] as const;
export type BadgeTone = (typeof BADGE_TONES)[number];

export interface BadgeProps {
    /** Defaults to `neutral`. There is deliberately no `info` — §2 has no such tone. */
    readonly tone?: BadgeTone;
    /** The category this badge names. Sentence case — see the note on the component. */
    readonly children: ReactNode;
    readonly 'data-testid'?: string;
}

/**
 * A filled pill naming a **category** — distinct from StatusIndicator, which names a *state* and is
 * a dot plus a label rather than a filled pill (§5 draws that line explicitly).
 *
 * **Sentence case, and this component does not transform it.** §5's Badge row carried two
 * provisional points pending the prototype on screen; both were settled at FS-3 subtask 8 by
 * measuring the prototype rather than by preference. Radius: its badges are a 20px corner on a
 * ~21px-tall pill, i.e. a stadium, so `radius/pill` as §5 reads it. Casing: the prototype contains
 * no `text-transform` at all and its labels are literal sentence case — "Brief complete",
 * "3 fields missing", "Bulk · auction-linked · weekly". So the label is whatever string the caller
 * passes, per §9's "sentence case everywhere", and `type/micro` supplies the METRICS only. That is
 * available precisely because the token family carries no casing: tokens.css defines micro as
 * size/weight/line/face/tracking and nothing else, so using it here overrides nothing and punches
 * no hole in §3's "one token resolves a whole level" contract.
 */
export function Badge({ tone = 'neutral', children, 'data-testid': testId }: BadgeProps) {
    return (
        <span className={`${styles.badge} ${styles[tone]}`} data-testid={testId}>
            {children}
        </span>
    );
}
